import { Injectable, inject } from '@angular/core';
import { OfflineStorageService } from './offline-storage.service';
import { PassportDetailsStore } from './passport-details.store';
import { SessionService } from '../core/session.service';

export interface OfflineHistoryEntry {
  historyID: number;
  passportID: number;
  action: string;
  roleID: number;
  roleName: string;
  createdByID: number;
  createdByEmployeeName: string;
  createdOn: string;
  submittedDateTime: string;
  updatedDateTime: string;
  reason: string;
  comment: string | null;
  shuntingYardID: number | null;
  oldValue: string;
  newValue: string;
  haccpChange: boolean;
  details: OfflineHistoryDetail[];
}

export interface OfflineHistoryDetail {
  historyId: number;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
}

@Injectable({ providedIn: 'root' })
export class OfflineHistoryService {
  private readonly offlineStorage = inject(OfflineStorageService);
  private readonly detailsStore = inject(PassportDetailsStore);
  private readonly session = inject(SessionService);

  // Use negative IDs starting from -1000000 to avoid conflict with backend's -1 for "Passport Created"
  private nextOfflineHistoryId = -1000000;

  // Store original snapshots of passport data when page loads (before user edits)
  // This is the most accurate baseline for history comparison
  private readonly originalSnapshots = new Map<string, any>();

  /**
   * Capture the original passport data when the page loads (before any user edits)
   * This should be called immediately after loading data from the API
   */
  setOriginalSnapshot(passportId: string, data: any): void {
    if (!passportId || !data) return;
    
    // Deep clone to prevent reference issues
    const snapshot = structuredClone(data);
    this.originalSnapshots.set(passportId, snapshot);
    console.log(`📸 Captured original snapshot for passport ${passportId}`);
  }

  /**
   * Clear the original snapshot when leaving the passport details page
   */
  clearOriginalSnapshot(passportId: string): void {
    this.originalSnapshots.delete(passportId);
    console.log(`🗑️ Cleared original snapshot for passport ${passportId}`);
  }

  /**
   * Get the original snapshot for a passport (if available)
   */
  getOriginalSnapshot(passportId: string): unknown {
    return this.originalSnapshots.get(passportId) ?? null;
  }

  /**
   * Create a history entry for a draft save
   */
  createDraftHistoryEntry(
    passportId: string,
    oldData: any,
    newData: any
  ): OfflineHistoryEntry | null {
    console.log('📝 createDraftHistoryEntry called with:', { passportId, oldData, newData });
    
    const passportIdNum = Number.parseInt(passportId, 10);
    const historyId = this.nextOfflineHistoryId--;
    const now = new Date().toISOString();

    // Get user info
    const userId = Number.parseInt(localStorage.getItem('userID') || '0', 10);
    const employeeName = localStorage.getItem('employeeName') || 'Unknown User';
    const roleId = this.getRoleIdForPersona();
    const roleName = this.getRoleName(roleId);

    // Build old and new value snapshots
    console.log('📝 Building snapshots for comparison...');
    const oldValues = this.buildHistoryValuesJson(oldData);
    const newValues = this.buildHistoryValuesJson(newData);

    // Detect changes and generate details
    const details = this.generateHistoryDetails(historyId, oldValues, newValues);

    // Check for HACCP image changes and add detail if images were added
    // Only check HACCP changes if we have meaningful old data (not just imageCount placeholder)
    // This prevents false positives when old data is minimal/missing
    const hasValidOldData = oldData && (
      oldData.bulkerNumber !== undefined ||
      oldData.authorisedBy !== undefined ||
      oldData.authoriserName !== undefined ||
      oldData.loadingDateTime !== undefined ||
      oldData.origin !== undefined ||
      (oldData.haccpImages && oldData.haccpImages.length > 0)
    );
    
    console.log('📝 HACCP check - hasValidOldData:', hasValidOldData, 'oldData keys:', oldData ? Object.keys(oldData) : 'null');
    
    let haccpChange = false;
    if (hasValidOldData) {
      haccpChange = this.detectHaccpChange(oldData, newData);
      const haccpImageDetail = this.generateHaccpImageDetail(historyId, oldData, newData, newData);
      if (haccpImageDetail) {
        details.push(haccpImageDetail);
      }
    } else {
      console.log('📝 Skipping HACCP change detection - no valid old data baseline');
    }

    // Determine action based on changes
    const action = this.determineAction(details);

    // Skip creating history if no meaningful changes
    if (action === 'SUBMISSION OMITTED' || details.length === 0) {
      console.log('📝 No meaningful changes detected, skipping offline history entry');
      return null;
    }

    return {
      historyID: historyId,
      passportID: passportIdNum,
      action,
      roleID: roleId,
      roleName,
      createdByID: userId,
      createdByEmployeeName: employeeName,
      createdOn: now,
      submittedDateTime: now,
      updatedDateTime: now,
      reason: '',
      comment: null,
      shuntingYardID: null,
      oldValue: JSON.stringify(oldValues),
      newValue: JSON.stringify(newValues),
      haccpChange,
      details
    };
  }

  /**
   * Build a JSON snapshot of passport fields for history comparison
   * Note: ScheduledDateTime is immutable (set by scheduler) and never changes, so it's not tracked
   * Note: HaccpCheck/images are tracked separately via generateHaccpImageDetail()
   * Note: RawSourceType is NOT included - origin changes are tracked via OriginName (which includes type prefix)
   * Note: AuthorisedByUserID is NOT included - authorizer changes are tracked via AuthorizerName
   */
  private buildHistoryValuesJson(data: any): Record<string, any> {
    if (!data) return {};

    const result = {
      TrailerNumber: data.bulkerNumber || data.trailerNumber || data.TrailerNumber || null,
      // AuthorisedByUserID removed - it's redundant with AuthorizerName and causes "undefined → N/A" issues
      AuthorizerName: this.getAuthorizerName(data),
      LoadingDateTime: data.loadingDateTime || data.LoadingDateTime || null,
      PlannedDateTime: data.plannedDateTime || data.PlannedDateTime || null,
      OriginName: this.extractOriginName(data.origin || data.Origin || data.originName)
    };

    console.log('📊 buildHistoryValuesJson input:', JSON.stringify(data, null, 2));
    console.log('📊 buildHistoryValuesJson output:', result);
    
    return result;
  }

  /**
   * Get AuthorisedByUserID from data
   */
  private getAuthorisedByUserID(data: any): number | null {
    // Check various field names for user ID
    const userId = data.authorisedByUserID ?? data.AuthorisedByUserID ?? 
                   data.authorizedByUserID ?? data.AuthorizedByUserID;
    if (userId != null) {
      return typeof userId === 'number' ? userId : Number.parseInt(userId, 10);
    }
    // If authorisedBy is a number or number string, it's a user ID
    const authorisedBy = data.authorisedBy ?? data.AuthorisedBy ?? data.authorizedBy ?? data.AuthorizedBy;
    if (authorisedBy != null) {
      const str = String(authorisedBy).trim();
      if (/^\d+$/.test(str)) {
        return Number.parseInt(str, 10);
      }
    }
    return null;
  }

  /**
   * Get Authorizer Name from data
   */
  private getAuthorizerName(data: any): string | null {
    // Check authoriserName / authorizerName first (this is the actual name)
    const name = data.authoriserName ?? data.AuthoriserName ?? 
                 data.authorizerName ?? data.AuthorizerName;
    if (name) {
      return String(name);
    }
    // Check if authorisedBy contains a name (not just a number)
    const authorisedBy = data.authorisedBy ?? data.AuthorisedBy ?? data.authorizedBy ?? data.AuthorizedBy;
    if (authorisedBy != null) {
      const str = String(authorisedBy).trim();
      // If it's NOT a pure number, it's likely a name
      if (!/^\d+$/.test(str)) {
        return str;
      }
    }
    return null;
  }

  /**
   * Parse HACCP check value
   */
  private parseHaccpCheck(value: any): boolean | null {
    if (value === true || value === 'Yes' || value === 'true' || value === 1) {
      return true;
    }
    if (value === false || value === 'No' || value === 'false' || value === 0) {
      return false;
    }
    return null;
  }

  /**
   * Extract raw source type from origin string
   */
  private extractRawSourceType(origin: any): string | null {
    if (!origin) return null;
    
    const originStr = typeof origin === 'string' ? origin : String(origin);
    const originLower = originStr.toLowerCase();
    if (originLower.startsWith('field:') || originLower.startsWith('field -')) return '1';
    if (originLower.startsWith('store:') || originLower.startsWith('store ')) return '2';
    if (originLower.startsWith('transloading:') || originLower.startsWith('transloading ')) return '3';
    if (originLower.startsWith('plant:') || originLower.startsWith('plant ')) return '4';
    
    return null;
  }

  /**
   * Extract origin name from origin string (removes type prefix)
   */
  private extractOriginName(origin: any): string | null {
    if (!origin) return null;
    
    // Handle non-string input
    const originStr = typeof origin === 'string' ? origin : String(origin);
    
    // Remove type prefix like "Field: ", "Store: ", etc.
    const parts = originStr.split(/:\s*|(?:Field|Store|Plant|Transloading)\s*-\s*/i);
    return parts.length > 1 ? (parts.at(-1) ?? '').trim() : originStr.trim();
  }

  /**
   * Determine the action based on changes
   */
  private determineAction(details: OfflineHistoryDetail[]): string {
    // If no field changes, it's an empty save
    if (details.length === 0) {
      return 'SUBMISSION OMITTED';
    }
    return 'Draft Saved';
  }

  /**
   * Detect HACCP image changes (added or removed)
   * Returns true if haccpChange should be set on the history entry
   */
  private detectHaccpChange(oldData: any, newData: any): boolean {
    const oldTotalImages = this.getTotalImageCount(oldData);
    const newTotalImages = this.getTotalImageCount(newData);
    
    console.log(`📊 HACCP change detection: old=${oldTotalImages}, new=${newTotalImages}`);
    
    return oldTotalImages !== newTotalImages;
  }

  /**
   * Get total effective image count
   * Handles different data formats:
   * - Form data: haccpImages array (may or may not include deleted), deletedImageFilenames, haccpImageFiles
   * - Backend history: imageCount only
   * - Cached draft: any combination
   */
  private getTotalImageCount(data: any): number {
    if (!data) return 0;
    
    // Count new offline files (File objects)
    const newFiles = data.haccpImageFiles?.length ?? 0;
    
    // Get deleted count
    const deletedCount = data.deletedImageFilenames?.length ?? 0;
    
    // Count placeholder images (from backend)
    const haccpImages = data.haccpImages || [];
    const placeholderCount = haccpImages.filter((img: string) => 
      img?.startsWith('PLACEHOLDER:')
    ).length;
    
    // Get backend image count (set when data was loaded)
    const backendCount = data.imageCount ?? data.haccpImageCount ?? 0;
    
    // Determine base count:
    // - If we have placeholders in array, use that count (images in array are NOT deleted)
    // - If no placeholders but have backendCount, use backendCount - deletedCount
    // - Otherwise use 0
    let baseCount: number;
    if (placeholderCount > 0) {
      // haccpImages array contains current visible placeholders (already excludes deleted)
      baseCount = placeholderCount;
    } else if (backendCount > 0) {
      // No placeholder array, use backend count minus deletions
      baseCount = Math.max(0, backendCount - deletedCount);
    } else {
      baseCount = 0;
    }
    
    const total = baseCount + newFiles;
    console.log(`📊 getTotalImageCount: placeholders=${placeholderCount}, backendCount=${backendCount}, deleted=${deletedCount}, newFiles=${newFiles}, total=${total}`);
    
    return total;
  }

  /**
   * Generate a history detail for HACCP image changes
   * - First upload (passport had NO images from backend) → "uploaded"
   * - Any change when passport already had images from backend → "modified"
   */
  private generateHaccpImageDetail(
    historyId: number,
    oldData: any,
    newData: any,
    originalFormData?: any  // Pass the original form data to check imageCount
  ): OfflineHistoryDetail | null {
    const oldCount = this.getTotalImageCount(oldData);
    const newCount = this.getTotalImageCount(newData);
    
    console.log(`📊 generateHaccpImageDetail: oldCount=${oldCount}, newCount=${newCount}`);
    console.log(`📊 generateHaccpImageDetail newData keys:`, newData ? Object.keys(newData) : 'null');
    console.log(`📊 generateHaccpImageDetail originalFormData keys:`, originalFormData ? Object.keys(originalFormData) : 'null');
    
    // No change in image count
    if (oldCount === newCount) {
      return null;
    }
    
    // Check if passport was loaded from backend with images
    // Check multiple sources: originalFormData, newData, oldData
    const imageCountFromForm = originalFormData?.imageCount ?? newData?.imageCount ?? oldData?.imageCount ?? 0;
    const backendHadImages = imageCountFromForm > 0;
    
    console.log(`📊 HACCP: backendHadImages=${backendHadImages} (imageCount=${imageCountFromForm})`);
    
    // Determine action:
    // - If backend had images, any change is "modified"
    // - If backend had NO images and now we're adding, it's "uploaded"
    const newValue = backendHadImages ? 'modified' : 'uploaded';
    
    console.log(`📊 HACCP RESULT: newValue=${newValue}`);
    
    return {
      historyId,
      fieldName: 'HaccpCheck',
      oldValue: oldCount > 0 ? `${oldCount} image(s)` : null,
      newValue
    };
  }

  /**
   * Generate history details by comparing old and new values
   */
  private generateHistoryDetails(
    historyId: number,
    oldValues: Record<string, any>,
    newValues: Record<string, any>
  ): OfflineHistoryDetail[] {
    const details: OfflineHistoryDetail[] = [];
    const allKeys = new Set([...Object.keys(oldValues), ...Object.keys(newValues)]);

    // Fields to skip in history (AuthorisedByUserID is internal, RawSourceType is metadata, PlannedDateTime is already excluded in backend)
    const skipFields = new Set(['AuthorisedByUserID', 'RawSourceType', 'PlannedDateTime']);

    console.log('📊 generateHistoryDetails comparing:', { oldValues, newValues });

    for (const key of allKeys) {
      if (skipFields.has(key)) continue;

      const oldVal = this.normalizeValue(oldValues[key]);
      const newVal = this.normalizeValue(newValues[key]);

      console.log(`📊 Comparing ${key}: "${oldVal}" vs "${newVal}" (equal: ${oldVal === newVal})`);

      // Check if this field should be skipped (based on final normalized string comparison)
      if (this.shouldSkipHistoryField(key, oldVal, newVal)) {
        continue;
      }

      details.push({
        historyId,
        fieldName: key,
        oldValue: oldVal || null,
        newValue: newVal || null
      });
    }

    return details;
  }

  /**
   * Determine if a field should be skipped in history comparison
   */
  private shouldSkipHistoryField(
    key: string,
    oldVal: string,
    newVal: string
  ): boolean {
    // Skip if both are empty
    if (oldVal === '' && newVal === '') return true;

    // Skip if values are the same after normalization (handles A→A cases)
    if (oldVal === newVal) return true;

    // Special case-insensitive comparison for AuthorizerName
    if (key === 'AuthorizerName' && oldVal.toLowerCase() === newVal.toLowerCase()) {
      return true;
    }

    return false;
  }

  /**
   * Normalize a value for comparison
   */
  private normalizeValue(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    
    // Normalize date strings to ISO format without milliseconds
    if (this.isDateString(value)) {
      return this.normalizeDateValue(String(value));
    }
    
    return String(value).trim();
  }

  /**
   * Check if a value looks like a date string
   */
  private isDateString(value: any): boolean {
    if (typeof value !== 'string') return false;
    // Check for ISO format or common date patterns
    return /^\d{4}-\d{2}-\d{2}/.test(value) || /^\d{1,2}\/\d{1,2}\/\d{4}/.test(value);
  }

  /**
   * Normalize date to consistent format for comparison
   * Strips timezone info, milliseconds, and seconds to compare just date and HH:mm
   * IMPORTANT: Does NOT convert between timezones - preserves the time as-is
   */
  private normalizeDateValue(value: string): string {
    try {
      let normalized = value.trim();
      
      // Remove milliseconds (e.g., .123, .1234567, .123Z)
      normalized = normalized.replace(/\.\d+/, '');
      
      // Remove trailing Z (UTC indicator)
      normalized = normalized.replace(/Z$/i, '');
      
      // Remove timezone offset like +05:30 or -08:00
      normalized = normalized.replace(/[+-]\d{2}:\d{2}$/, '');
      
      // Remove seconds if present (e.g., T10:00:00 -> T10:00, T10:00:30 -> T10:00)
      // This ensures YYYY-MM-DDTHH:mm:ss and YYYY-MM-DDTHH:mm are treated as equal
      normalized = normalized.replace(/T(\d{2}:\d{2}):\d{2}$/, 'T$1');
      
      // At this point we should have YYYY-MM-DDTHH:mm
      return normalized;
    } catch {
      // Fall through
    }
    return value;
  }

  /**
   * Get role ID for current persona
   */
  private getRoleIdForPersona(): number {
    const persona = (this.session.persona() || '').toLowerCase();
    const roleMap: Record<string, number> = {
      'admin': 1,
      'grower': 2,
      'transloader': 3,
      'receiver': 4,
      'shunter': 5,
      'shunting': 5,
      'scheduler': 6,
      'gate house': 7,
      'gatehouse': 7
    };
    
    for (const [key, value] of Object.entries(roleMap)) {
      if (persona.includes(key)) return value;
    }
    return 2; // Default to Grower
  }

  /**
   * Get role name from role ID
   */
  private getRoleName(roleId: number): string {
    const names: Record<number, string> = {
      1: 'Admin',
      2: 'Grower',
      3: 'Transloader',
      4: 'Receiver',
      5: 'Shunting',
      6: 'Scheduler',
      7: 'Gate House'
    };
    return names[roleId] || 'Unknown';
  }

  /**
   * Save a history entry to offline storage
   */
  async saveHistoryEntry(passportId: string, entry: OfflineHistoryEntry): Promise<void> {
    const passportIdNum = Number.parseInt(passportId, 10);
    
    // Get existing history
    const existingHistory = await this.offlineStorage.getHistory(passportIdNum);
    
    // Add new entry at the beginning (most recent first)
    const updatedHistory = [entry, ...(existingHistory || [])];
    
    // Save back to IndexedDB
    await this.offlineStorage.saveHistory(passportIdNum, updatedHistory);
    
    // Also update the detailsStore
    const currentDetails = this.detailsStore.get(passportId);
    if (currentDetails) {
      const currentHistory = currentDetails.history || [];
      this.detailsStore.update(passportId, {
        history: [entry, ...currentHistory]
      });
    }
    
    console.log(`📝 Saved offline history entry for passport ${passportId}, historyID: ${entry.historyID}`);
  }

  /**
   * Get old data for history comparison
   * For FIRST offline save: Use original snapshot (captured when page loaded) as primary source
   * For SUBSEQUENT offline saves: Use the last cached draft
   * @param passportId - The passport ID
   * @param currentFormData - The current form data (used for extracting history as fallback)
   */
  async getOldDataForHistory(passportId: string, currentFormData?: any): Promise<any> {
    const passportIdNum = Number.parseInt(passportId, 10);
    
    // Check if there are any existing offline history entries (negative IDs)
    const existingHistory = await this.offlineStorage.getHistory(passportIdNum);
    const hasOfflineHistory = existingHistory?.some((h: any) => h.historyID <= -1000000);
    
    console.log(`📊 getOldDataForHistory: hasOfflineHistory=${hasOfflineHistory}, passportId=${passportId}`);
    
    if (hasOfflineHistory) {
      // SUBSEQUENT offline save - use the last cached draft
      const cachedDraft = await this.offlineStorage.getDraft(passportId);
      if (cachedDraft) {
        console.log('📊 getOldDataForHistory: Using cached draft (subsequent offline save)');
        console.log('📊 Cached draft keys:', Object.keys(cachedDraft));
        return this.convertCachedPassportToHistoryFormat(cachedDraft);
      }
    }
    
    // FIRST offline save - use original data from when page loaded
    // Priority: Original snapshot > Backend history > IndexedDB passport > null
    // EXCEPTION: For transferred loads edited by a Scheduler, backend history (Scheduler role) wins over original snapshot (Grower role)
    
    const isTransferredScheduler = currentFormData?.transferFlag === true && this.getRoleIdForPersona() === 6;
    
    if (isTransferredScheduler) {
      const backendHistoryResult = this.tryGetFromBackendHistory(currentFormData);
      if (backendHistoryResult) {
        console.log('📊 getOldDataForHistory: Using backend history for transferred Scheduler load');
        return backendHistoryResult;
      }
    }
    
    // 1. PRIMARY: Check original snapshot captured when page loaded
    const originalSnapshot = this.originalSnapshots.get(passportId);
    if (originalSnapshot) {
      console.log('📊 getOldDataForHistory: Using original snapshot (captured when page loaded)');
      console.log('📊 Original snapshot keys:', Object.keys(originalSnapshot));
      return this.convertCachedPassportToHistoryFormat(originalSnapshot);
    }
    
    // 2. FALLBACK: Check backend history from the current form data
    if (!isTransferredScheduler) {
      const backendHistoryResult = this.tryGetFromBackendHistory(currentFormData);
      if (backendHistoryResult) {
        return backendHistoryResult;
      }
    }
    
    // 3. FALLBACK: Check IndexedDB PASSPORTS store for original backend data
    const cachedPassport = await this.offlineStorage.getPassport(passportIdNum);
    if (cachedPassport) {
      console.log('📊 getOldDataForHistory: Using IndexedDB passport cache (fallback - may be stale LIST data)');
      console.log('📊 Cached passport keys:', Object.keys(cachedPassport));
      return this.convertCachedPassportToHistoryFormat(cachedPassport);
    }
    
    // 4. Last resort: Use currentFormData to at least preserve imageCount for HACCP tracking
    if (currentFormData?.imageCount != null || currentFormData?.haccpImageLinks?.length > 0) {
      console.log('📊 getOldDataForHistory: Using imageCount from currentFormData as minimal baseline');
      return {
        imageCount: currentFormData.imageCount ?? currentFormData.haccpImageLinks?.length ?? 0,
        haccpImages: [],
        haccpImageFiles: [],
        deletedImageFilenames: []
      };
    }

    console.log('📊 getOldDataForHistory: No old data found (first time save - no snapshot/history available)');
    return null;
  }

  /**
   * Try to get old data from backend history in currentFormData
   * Returns null if not available or parsing fails
   */
  private tryGetFromBackendHistory(currentFormData?: any): any {
    if (!currentFormData?.history?.length) {
      return null;
    }
    
    const currentRoleId = this.getRoleIdForPersona();
    console.log(`📊 getOldDataForHistory: Searching backend history for roleId=${currentRoleId}`);
    
    // Filter all history entries that match the current role, then get the LAST one (most recent)
    const roleMatchingEntries = currentFormData.history.filter(
      (h: any) => (h.roleID === currentRoleId || h.roleId === currentRoleId)
    );
    const roleMatchingEntry = roleMatchingEntries.at(-1);
    
    if (!roleMatchingEntry?.newValues) {
      console.log(`📊 getOldDataForHistory: No history entry found for roleId=${currentRoleId}`);
      return null;
    }
    
    console.log(`📊 getOldDataForHistory: Using backend history newValues as baseline (roleId=${currentRoleId}, found ${roleMatchingEntries.length} entries, using last)`);
    try {
      const backendValues = typeof roleMatchingEntry.newValues === 'string' 
        ? JSON.parse(roleMatchingEntry.newValues) 
        : roleMatchingEntry.newValues;
      console.log('📊 Parsed backendValues:', JSON.stringify(backendValues));
      return this.convertBackendHistoryToHistoryFormat(backendValues, currentFormData);
    } catch (e) {
      console.warn('📊 Failed to parse backend history:', e);
      return null;
    }
  }

  /**
   * Convert backend history values to format suitable for comparison
   * IMPORTANT: For HACCP images, use imageCount (original count from backend load)
   * NOT currentFormData.haccpImages (which reflects current deletions)
   */
  private convertBackendHistoryToHistoryFormat(backendValues: any, currentFormData?: any): any {
    // Get the ORIGINAL image count from when data was loaded from backend
    // This represents the state BEFORE user made any deletions
    const originalImageCount = currentFormData?.imageCount ?? currentFormData?.haccpImageCount ?? 0;
    
    return {
      bulkerNumber: backendValues.TrailerNumber,
      haccpCheck: backendValues.HaccpCheck,
      authorisedByUserID: backendValues.AuthorisedByUserID,
      authorisedBy: backendValues.AuthorizerName,
      authoriserName: backendValues.AuthorizerName,
      loadingDateTime: backendValues.LoadingDateTime,
      plannedDateTime: backendValues.PlannedDateTime,
      scheduledDateTime: backendValues.ScheduledDateTime || currentFormData?.dueDateTime,
      origin: this.buildOriginFromBackendValues(backendValues),
      // Use imageCount to represent original state, not current haccpImages array
      haccpImages: [],
      haccpImageFiles: [],
      deletedImageFilenames: [],
      imageCount: originalImageCount  // This is used by getTotalImageCount
    };
  }

  /**
   * Build origin string from backend history values
   */
  private buildOriginFromBackendValues(backendValues: any): string | null {
    if (!backendValues.OriginName) return null;
    const type = backendValues.RawSourceType;
    const prefix = this.getOriginTypePrefix(type);
    return prefix ? `${prefix}${backendValues.OriginName}` : backendValues.OriginName;
  }

  /**
   * Convert cached passport to format suitable for history comparison
   */
  private convertCachedPassportToHistoryFormat(data: any): any {
    // Get image count from various possible sources
    const imageCount = data.imageCount ?? 
                       data.haccpImageCount ?? 
                       data.haccpImageLinks?.length ?? 
                       data.HaccpImageLinks?.length ?? 
                       data.haccpImages?.length ?? 
                       0;
    
    return {
      bulkerNumber: data.trailerNumber || data.bulkerNumber || data.TrailerNumber || data.BulkerNumber,
      haccpCheck: this.parseHaccpCheck(data.haccpCheck ?? data.HaccpCheck),
      authorisedByUserID: data.authorisedByUserID || data.authorizedByUserID || data.AuthorisedByUserID,
      authorisedBy: data.authorisedBy || data.authorizedBy || data.AuthorisedBy,
      authoriserName: data.authoriserName || data.authorizerName || data.AuthorizerName,
      loadingDateTime: data.loadingDateTime || data.LoadingDateTime,
      plannedDateTime: data.plannedDateTime || data.PlannedDateTime,
      scheduledDateTime: data.scheduledDateTime || data.ScheduledDateTime || data.dueDateTime,
      origin: this.formatOriginForHistory(data),
      haccpImages: data.haccpImages || data.HaccpImages || data.haccpImageLinks || data.HaccpImageLinks || [],
      haccpImageFiles: data.haccpImageFiles || [],
      deletedImageFilenames: data.deletedImageFilenames || [],
      imageCount: imageCount  // Preserve original image count for HACCP change detection
    };
  }

  /**
   * Format origin for history
   */
  private formatOriginForHistory(data: any): string | null {
    if (data.origin) return data.origin;
    if (data.originName) {
      const type = data.rawSourceType || data.RawSourceType;
      const typePrefix = this.getOriginTypePrefix(type);
      return typePrefix ? `${typePrefix}${data.originName}` : data.originName;
    }
    return null;
  }

  /**
   * Get origin type prefix
   */
  private getOriginTypePrefix(type: string | null | undefined): string {
    if (!type) return '';
    switch (type) {
      case '1': return 'Field: ';
      case '2': return 'Store: ';
      case '3': return 'Transloading: ';
      case '4': return 'Plant: ';
      default: return '';
    }
  }

  /**
   * Get all pending offline history entries (those with negative IDs that haven't been synced)
   * Returns entries grouped by passport ID for efficient syncing
   */
  async getAllPendingOfflineHistories(): Promise<OfflineHistoryEntry[]> {
    const allHistories: OfflineHistoryEntry[] = [];
    
    try {
      // Get all passports that have history stored
      const allPassportHistories = await this.offlineStorage.getAllHistories();
      
      for (const passportHistory of allPassportHistories) {
        if (passportHistory.history && Array.isArray(passportHistory.history)) {
          // Filter for entries with negative IDs (offline-created entries) AND valid createdByID
          const pendingEntries = passportHistory.history.filter(
            (entry: OfflineHistoryEntry) => 
              entry.historyID <= -1000000 && 
              entry.createdByID != null && 
              entry.createdByID > 0
          );
          allHistories.push(...pendingEntries);
        }
      }
      
      console.log(`📝 Found ${allHistories.length} pending offline history entries to sync`);
      return allHistories;
    } catch (error) {
      console.warn('❌ Error getting pending offline histories:', error);
      return [];
    }
  }

  /**
   * Remove synced history entries from IndexedDB
   * Called after successful sync to clean up local storage
   */
  async removeSyncedHistoryEntries(syncedHistoryIds: number[]): Promise<void> {
    if (syncedHistoryIds.length === 0) return;
    
    try {
      const allPassportHistories = await this.offlineStorage.getAllHistories();
      
      for (const passportHistory of allPassportHistories) {
        if (passportHistory.history && Array.isArray(passportHistory.history)) {
          // Filter out the synced entries
          const remainingEntries = passportHistory.history.filter(
            (entry: OfflineHistoryEntry) => !syncedHistoryIds.includes(entry.historyID)
          );
          
          // Update IndexedDB with remaining entries
          if (remainingEntries.length !== passportHistory.history.length) {
            await this.offlineStorage.saveHistory(passportHistory.passportID, remainingEntries);
            console.log(`🗑️ Removed ${passportHistory.history.length - remainingEntries.length} synced history entries for passport ${passportHistory.passportID}`);
          }
        }
      }
    } catch (error) {
      console.warn('❌ Error removing synced history entries:', error);
    }
  }

  /**
   * Convert OfflineHistoryEntry to the format expected by the backend API
   */
  convertToBackendFormat(entry: OfflineHistoryEntry): {
    passportID: number;
    roleID: number;
    createdByID: number;
    createdOn: string;
    reason: string;
    comment: string | null;
    shuntingYardID: number | null;
    oldValue: string;
    newValue: string;
    haccpChange: boolean;
    details: Array<{ fieldName: string; oldValue: string | null; newValue: string | null }>;
  } {
    // Get current user ID as fallback (should never be needed if getAllPendingOfflineHistories filters correctly)
    const currentUserId = Number.parseInt(localStorage.getItem('userID') || '0', 10);
    
    return {
      passportID: entry.passportID,
      roleID: entry.roleID,
      createdByID: entry.createdByID || currentUserId,
      createdOn: entry.createdOn,
      reason: entry.reason || '',
      comment: entry.comment,
      shuntingYardID: entry.shuntingYardID,
      oldValue: entry.oldValue || '{}',
      newValue: entry.newValue || '{}',
      haccpChange: entry.haccpChange,
      details: (entry.details || []).map(d => ({
        fieldName: d.fieldName,
        oldValue: d.oldValue,
        newValue: d.newValue
      }))
    };
  }
}

